import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import "./CommercialDashboard.css";
import Header from "../../common/Header";
import Footer from "../../common/Footer";
import PageWrapper from "../../common/PageWrapper";
import { useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import DocumentModal from "../../Forms/PdfForm/DocumentModal";


const CommercialDashboard = ({ menuState }) => {
  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5002/api";
  const { isMenuOpen, setIsMenuOpen, onMenuToggle } = menuState || {};

  const [vehicules, setVehicules] = useState([]);
  const [filteredVehicules, setFilteredVehicules] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDocumentModalOpen, setIsDocumentModalOpen] = useState(false);
  const [selectedVehiculeId, setSelectedVehiculeId] = useState(null);

  const [vehiculeFilters, setVehiculeFilters] = useState({
    statut: "",
    numero_dossier: "",
    numero_chassis: "",
    vendeur: "",
    nom_client: "",
  });

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const token = localStorage.getItem("token");

        if (!token) {
          console.error("Token d'authentification manquant");
          throw new Error("Authentification requise");
        }

        let userData;
        try {
          const userResponse = await fetch(`${API_URL}/users/me`, {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          });

          if (!userResponse.ok) {
            throw new Error("Erreur lors de la récupération des informations utilisateur");
          }

          userData = await userResponse.json();
          console.log("👤 Utilisateur connecté:", userData);
        } catch (userError) {
          console.error("Erreur lors de la récupération de l'utilisateur:", userError);
          throw new Error("Impossible de récupérer vos informations");
        }

        if (!userData || !userData.concessions_id) {
          console.log("Utilisateur sans concession:", userData);
          throw new Error("Aucune concession associée à votre compte");
        }

        let vehiculesData;
        try {
          const vehiculesResponse = await fetch(
            `${API_URL}/vehicules?concession_id=${userData.concessions_id}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              }
            }
          );

          if (!vehiculesResponse.ok) {
            throw new Error("Erreur lors du chargement des véhicules");
          }

          vehiculesData = await vehiculesResponse.json();
          console.log("🚗 Véhicules récupérés:", vehiculesData);
        } catch (vehiculesError) {
          console.error("Erreur lors de la récupération des véhicules:", vehiculesError);
          throw new Error("Impossible de charger les véhicules");
        }

        let vehiculesList = vehiculesData;

        if (vehiculesData && vehiculesData.data && Array.isArray(vehiculesData.data)) {
          vehiculesList = vehiculesData.data;
        }
        else if (!Array.isArray(vehiculesData)) {
          console.log("Données de véhicules invalides:", vehiculesData);
          setError("Aucun véhicule dans votre concession");
          setVehicules([]);
          setFilteredVehicules([]);
          return;
        }

        if (vehiculesList.length === 0) {
          console.log("Aucun véhicule trouvé");
          setError("Aucun véhicule dans votre concession");
          setVehicules([]);
          setFilteredVehicules([]);
        } else {
          console.log(`${vehiculesList.length} véhicules trouvés au total`);

          const concessionVehicules = vehiculesList.filter(
            (vehicule) => vehicule && vehicule.concession_id === userData.concessions_id
          );

          console.log(`${concessionVehicules.length} véhicules appartenant à la concession ${userData.concessions_id}`);

          if (concessionVehicules.length === 0) {
            setError("Aucun véhicule dans votre concession");
            setVehicules([]);
            setFilteredVehicules([]);
          } else {
            setVehicules(concessionVehicules);
            setFilteredVehicules(concessionVehicules);
            setError(null);
          }
        }
      } catch (error) {
        console.error("Erreur globale:", error);
        setError(error.message || "Une erreur est survenue");
        setVehicules([]);
        setFilteredVehicules([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();

    return () => {
    };
  }, [API_URL, location]);

  const filterVehicules = () => {
    if (!Array.isArray(vehicules)) {
      console.warn("filterVehicules: vehicules n'est pas un tableau", vehicules);
      return [];
    }

    return vehicules.filter((vehicule) => {
      if (!vehicule) return false;

      const statutMatch =
        !vehiculeFilters.statut || vehicule.statut === vehiculeFilters.statut;

      const dossierMatch =
        !vehiculeFilters.numero_dossier ||
        (vehicule.numero_dossier
          ?.toLowerCase()
          ?.includes(vehiculeFilters.numero_dossier.toLowerCase()) ?? false);

      const chassisMatch =
        !vehiculeFilters.numero_chassis ||
        (vehicule.numero_chassis
          ?.toLowerCase()
          ?.includes(vehiculeFilters.numero_chassis.toLowerCase()) ?? false);

      const vendeurMatch =
        !vehiculeFilters.vendeur ||
        (vehicule.vendeur_nom
          ?.toLowerCase()
          ?.includes(vehiculeFilters.vendeur.toLowerCase()) ?? false);

      const clientMatch =
        !vehiculeFilters.nom_client ||
        (vehicule.nom_client
          ?.toLowerCase()
          ?.includes(vehiculeFilters.nom_client.toLowerCase()) ?? false);

      return (
        statutMatch &&
        dossierMatch &&
        chassisMatch &&
        vendeurMatch &&
        clientMatch
      );
    });
  };

  const handleSearch = () => {
    try {
      const results = filterVehicules();

      if (!Array.isArray(results)) {
        console.error("Résultats de filtrage invalides:", results);
        return;
      }

      setFilteredVehicules(results);

      if (results.length === 0 && Array.isArray(vehicules) && vehicules.length > 0) {
        setError("Aucun véhicule ne correspond à vos critères de recherche");
      } else {
        setError(null);
      }
    } catch (error) {
      console.error("Erreur lors de la recherche:", error);
      setError("Une erreur est survenue lors de la recherche");
    }
  };

  const handleViewVehicule = (vehiculeId) => {
    navigate(`/vehicule/${vehiculeId}`);
  };

  const handleOpenDocumentModal = (vehiculeId) => {
    setSelectedVehiculeId(vehiculeId);
    setIsDocumentModalOpen(true);
  };

  const resetFilters = () => {
    try {
      setVehiculeFilters({
        statut: "",
        numero_dossier: "",
        numero_chassis: "",
        vendeur: "",
        nom_client: "",
      });

      if (!Array.isArray(vehicules)) {
        console.warn("resetFilters: vehicules n'est pas un tableau", vehicules);
        setFilteredVehicules([]);
        return;
      }

      setFilteredVehicules(vehicules);
      setError(null);
    } catch (error) {
      console.error("Erreur lors de la réinitialisation des filtres:", error);
      setError("Erreur lors de la réinitialisation des filtres");
    }
  };

  return (
    <div className="commercial-dashboard-wrapper">
      <Header
        isMenuOpen={isMenuOpen}
        setIsMenuOpen={setIsMenuOpen}
        onMenuToggle={onMenuToggle}
      />
      <PageWrapper isMenuOpen={isMenuOpen}>
        <div className="commercial-dashboard-content">
          {error && <div className="error-message">{error}</div>}
          {isLoading ? (
            <div className="loading">Chargement...</div>
          ) : (
            <section className="commercial-vehicules-section">
              <div className="commercial-section-header">
                <h2>Mes véhicules</h2>
              </div>

              <div className="commercial-search-filters">
                <div className="commercial-filters-row">
                  <div className="commercial-filter-cell">
                    <label>Statut</label>
                    <select
                      value={vehiculeFilters.statut}
                      onChange={(e) =>
                        setVehiculeFilters({
                          ...vehiculeFilters,
                          statut: e.target.value,
                        })
                      }
                    >
                      <option value="">Tous</option>
                      <option value="Commande">Commande</option>
                      <option value="Stock">Stock</option>
                      <option value="Vendu">Vendu</option>
                      <option value="Demo">Démo</option>
                    </select>
                  </div>
                  <div className="commercial-filter-cell">
                    <label>N° de châssis</label>
                    <input
                      type="text"
                      value={vehiculeFilters.numero_chassis}
                      onChange={(e) =>
                        setVehiculeFilters({
                          ...vehiculeFilters,
                          numero_chassis: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div className="commercial-filter-cell">
                    <label>N° de dossier</label>
                    <input
                      type="text"
                      value={vehiculeFilters.numero_dossier}
                      onChange={(e) =>
                        setVehiculeFilters({
                          ...vehiculeFilters,
                          numero_dossier: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>

                <div className="commercial-filters-row">
                  <div className="commercial-filter-cell">
                    <label>Vendeur</label>
                    <input
                      type="text"
                      value={vehiculeFilters.vendeur}
                      onChange={(e) =>
                        setVehiculeFilters({
                          ...vehiculeFilters,
                          vendeur: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div className="commercial-filter-cell">
                    <label>Nom du client</label>
                    <input
                      type="text"
                      value={vehiculeFilters.nom_client}
                      onChange={(e) =>
                        setVehiculeFilters({
                          ...vehiculeFilters,
                          nom_client: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div className="commercial-filter-cell actions">
                    <button
                      className="commercial-reset-button"
                      onClick={resetFilters}
                    >
                      Réinitialiser
                    </button>
                    <button
                      className="commercial-search-button"
                      onClick={handleSearch}
                    >
                      Rechercher
                    </button>
                  </div>
                </div>
              </div>

              <div className="commercial-table-container">
                <table className="commercial-data-table">
                  <thead>
                    <tr>
                      <th>STATUT</th>
                      <th>NUMÉRO DE DOSSIER</th>
                      <th>NUMÉRO DE CHASSIS</th>
                      <th>NOM DU CLIENT</th>
                      <th>NOM DU VENDEUR</th>
                      <th>ACTIONS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredVehicules.length > 0 ? (
                      filteredVehicules.map((vehicule) => (
                        <tr key={vehicule.id}>
                          <td>
                          <span className="status-label">{vehicule.statut}</span>

                          </td>
                          <td>{vehicule.numero_dossier}</td>
                          <td>{vehicule.numero_chassis}</td>
                          <td>{vehicule.nom_client}</td>
                          <td>{vehicule.vendeur_nom}</td>
                          <td className="commercial-actions-cell">
                            <div className="tooltip-container">
                              <button
                                className="commercial-view-button"
                                onClick={() => handleViewVehicule(vehicule.id)}
                              >
                                👁️
                              </button>
                              <span className="tooltip-text">Voir</span>
                            </div>
                            <div className="tooltip-container">
                              <button
                                className="commercial-download-button"
                                onClick={() => handleOpenDocumentModal(vehicule.id)}
                              >
                                📥
                              </button>
                              <span className="tooltip-text">Télécharger</span>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" className="no-vehicles-message">
                          Aucun véhicule dans votre concession
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          )}
          <DocumentModal
            show={isDocumentModalOpen}
            onHide={() => setIsDocumentModalOpen(false)}
            vehiculeId={selectedVehiculeId}
          />
        </div>
      </PageWrapper>
      <Footer />
    </div>
  );
};

CommercialDashboard.propTypes = {
  menuState: PropTypes.shape({
    isMenuOpen: PropTypes.bool,
    setIsMenuOpen: PropTypes.func,
    onMenuToggle: PropTypes.func,
  }),
};

export default CommercialDashboard;
